<template>
    <div
        v-if="umaiBanners.length > 0"
        id="sports-banner-el"
        v-bind:class="{
            Mobile_SportsLobby_Billboard: bannerPosition === BannerPosition.CastorTop,
            Mobile_MyBets_Billboard: bannerPosition === BannerPosition.MyBetsTop,
        }"
    >
        <section class="carousel-wrap-section">
            <Carousel
                v-show="isShowBanner"
                v-bind:autoplay="umaiBanners.length > 1 ? 5000 : 0"
                v-bind:mouseDrag="true"
                v-bind:wrapAround="umaiBanners.length > 1"
            >
                <Slide
                    v-for="item in umaiBanners"
                    v-bind:key="item.key"
                >
                    <div>
                        <img
                            v-bind:data-img="item.gtmTag"
                            v-bind:src="item.imagePath"
                            style="width: 100%; margin-bottom: -0.5rem;"
                            alt=""
                            aria-hidden="true"
                            v-on:click="item.onClick()"
                        >
                    </div>
                </Slide>
            </Carousel>
        </section>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch } from 'vue';
    import type { PropType } from 'vue';
    import { Carousel, Slide } from 'vue3-carousel';
    import type { IBannerDisplayInfo, IBannerInfo } from '@/interface/IBannerInfo';
    import {
        BannerPosition, BrandType,
    } from '@/interface/enum';
    import Api from '@/libraries/api';
    import useState from '@/libraries/composables/useState';
    import { useUmaiBanner } from '@/libraries/composables/useUmaiBanner';
    import { i18n } from '@/libraries/i18n';
    import { getDeviceOs } from '@/libraries/utils/userAgent';
    import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';
    import { useRootStore } from '@/stores/rootStore';

    export default defineComponent({
        name: 'SportsBanner',
        components: {
            Carousel,
            Slide,
        },
        props: {
            bannerPosition: {
                type: Number as PropType<BannerPosition>,
                required: true,
            },
        },
        setup(props) {
            const { setState } = useHeaderDisplayStore();
            const [isShowBanner, setIsShowBanner] = useState(true);

            const { executeBannerAction } = useUmaiBanner();
            const umaiBannerInfos = ref<IBannerInfo[]>([]);

            const umaiBanners = computed(() => {
                const getGtm = (url: string) => url?.split('/').pop()?.split('?').shift() ?? url;
                return umaiBannerInfos.value.map<IBannerDisplayInfo>(banner => ({
                    imagePath: banner.cdn,
                    gtmTag: getGtm(banner.cdn),
                    key: banner.cdn,
                    trackingCode: banner.trackingCode ?? '',
                    onClick: () => executeBannerAction(banner),
                }));
            });

            const { brand } = storeToRefs(useRootStore());
            async function getUmaiBannerInfos() {
                try {
                    const bannerRequest = {
                        brand: BrandType.toPollux(brand.value),
                        position: BannerPosition.toPollux(props.bannerPosition),
                        languageType: i18n.locale.value,
                        deviceOs: getDeviceOs(),
                    };
                    umaiBannerInfos.value = await Api.getBannerFromUmai(bannerRequest);
                } catch (e) {
                    console.error(e);
                    umaiBannerInfos.value = [];
                }
            }

            function setHeight(height?: number) {
                setState({ bannerHeight: height || 0 });
            }

            function updateDomHeight() {
                const el = document.querySelector('#sports-banner-el');
                setHeight(el?.clientHeight);
            }

            async function updateDomHeightAfterRender() {
                await nextTick();
                updateDomHeight();
            }

            onMounted(() => updateDomHeight());
            onUpdated(() => updateDomHeightAfterRender());
            onBeforeUnmount(() => setHeight(0));

            onMounted(async () => {
                // workaround for vue-carousel issue on IOS: https://github.com/SSENSE/vue-carousel/issues/614
                setIsShowBanner(false);
                await nextTick();
                setIsShowBanner(true);
            });

            watch(() => i18n.locale.value, () => {
                getUmaiBannerInfos();
            }, { immediate: true });

            return {
                BannerPosition,
                isShowBanner,
                umaiBanners,
                updateDomHeightAfterRender,
            };
        },
    });
</script>

<style lang="scss">
@import "vue3-carousel/dist/carousel.css";

#sports-banner-el .carousel,
#sports-banner-el .carousel__track {
    max-width: initial !important;
    padding-left: 0 !important;
}

.Mobile_SportsLobby_Billboard {
    background: #14346d;
}

.Mobile_MyBets_Billboard {
    padding-top: 12px;

    img {
        min-height: 40px;
    }
}
</style>
